/**
 * Chip layer — replaces packages/amgsicbo_v2/lib/presentation/flame/dragontiger_flame_game.dart
 * (class is named SicboFlameGame despite the filename).
 *
 * Coordinate system: 1792×828 reference (PixiLayer's logical size).
 *
 * SicBo chip placement uses the user's tap position (captured by a pointer
 * listener over the stage) with ±14px X / ±11px Y jitter, NOT a per-bet
 * scatter rect. The Rive betting artboard decides which bet area was hit
 * (it emits a `placeBetClick` event with `betID`), and we visually drop the
 * chip at the actual pointer coordinates.
 *
 * End-of-round chip-fly targets (dragontiger_flame_game.dart:274-276):
 *   Dealer (player lose): (50%, 20%)   = (896, 165.6)
 *   Player (player win):  (50%, 110%)  = (896, 910.8)  — off-screen below
 * Tie / half-half: first half → dealer, second half → player.
 *
 * Animations: 0.3s placement; 0.5s end-of-round with stagger 0.02s × index,
 * opacity fades to 0.2; chips destroyed 600 ms after trigger.
 */
import { Container } from 'pixi.js';
import { ChipSprite, preloadCoinTextures } from './chipSprite';
import { Easings, tween } from './tween';
import { REF_HEIGHT, REF_WIDTH } from '../config';

interface XY {
  x: number;
  y: number;
}

const PLAYER_ORIGIN: XY = { x: REF_WIDTH / 2, y: REF_HEIGHT + 50 };
const DEALER_TARGET: XY = { x: REF_WIDTH * 0.5, y: REF_HEIGHT * 0.2 };
const PLAYER_WIN_TARGET: XY = { x: REF_WIDTH * 0.5, y: REF_HEIGHT * 1.1 };

const JITTER_X = 14;
const JITTER_Y = 11;

interface ActiveChip {
  /** Bet item id this chip was placed on (for selective animations). */
  betId: number;
  sprite: ChipSprite;
}

export class ChipLayer {
  private readonly root: Container;
  private active: ActiveChip[] = [];

  constructor(parent: Container) {
    this.root = new Container();
    this.root.label = 'ChipLayer';
    parent.addChild(this.root);
    void preloadCoinTextures();
  }

  /**
   * Add a chip at the tap position (with jitter). Falls back to center if
   * no tap was captured.
   *   `tap` is in 1792×828 logical coords.
   *   `chipIndex` selects the coin colour (imgCoins{(index%6)+1}.png),
   *   matching ChipsSelection.
   */
  placeChip(
    betId: number,
    chipValue: number,
    chipIndex: number,
    tap: XY | null,
  ): void {
    const target: XY = tap
      ? {
          x: clamp(tap.x + (Math.random() * 2 - 1) * JITTER_X, 0, REF_WIDTH),
          y: clamp(tap.y + (Math.random() * 2 - 1) * JITTER_Y, 0, REF_HEIGHT),
        }
      : { x: REF_WIDTH / 2, y: REF_HEIGHT / 2 };

    const sprite = new ChipSprite(chipValue, chipIndex);
    sprite.position.set(PLAYER_ORIGIN.x, PLAYER_ORIGIN.y);
    this.root.addChild(sprite);

    const from = { ...PLAYER_ORIGIN };
    tween({
      durationMs: 300,
      easing: Easings.linear,
      onUpdate: (t) => {
        sprite.position.set(
          from.x + (target.x - from.x) * t,
          from.y + (target.y - from.y) * t,
        );
      },
    });

    this.active.push({ betId, sprite });
  }

  /**
   * Player wins overall. Animates winning-bet chips to player target,
   * losing-bet chips to dealer target. Auto-destroys after 600 ms.
   */
  animateWinningChips(winningBetIds: ReadonlySet<number>): void {
    const oldActive = this.active;
    this.active = [];
    oldActive.forEach((c, i) => {
      const target = winningBetIds.has(c.betId) ? PLAYER_WIN_TARGET : DEALER_TARGET;
      this.flyAndFade(c, target, i);
    });
    this.scheduleDestroyAll(oldActive, 600);
  }

  animateLosingChips(): void {
    const oldActive = this.active;
    this.active = [];
    oldActive.forEach((c, i) => this.flyAndFade(c, DEALER_TARGET, i));
    this.scheduleDestroyAll(oldActive, 600);
  }

  /** Half-half tie animation (first half → dealer, second half → player). */
  animateTie(): void {
    const oldActive = this.active;
    this.active = [];
    const half = Math.ceil(oldActive.length / 2);
    oldActive.forEach((c, i) => {
      const target = i < half ? DEALER_TARGET : PLAYER_WIN_TARGET;
      this.flyAndFade(c, target, i);
    });
    this.scheduleDestroyAll(oldActive, 600);
  }

  hardClear(): void {
    for (const c of this.active) c.sprite.destroy();
    this.active = [];
  }

  setOpacity(alpha: number): void {
    this.root.alpha = clamp(alpha, 0, 1);
  }

  hasAnyChips(): boolean {
    return this.active.length > 0;
  }

  private flyAndFade(c: ActiveChip, target: XY, index: number): void {
    const fromX = c.sprite.position.x;
    const fromY = c.sprite.position.y;
    const fromAlpha = c.sprite.alpha;
    const delayMs = 20 * index;

    window.setTimeout(() => {
      tween({
        durationMs: 500,
        easing: Easings.linear,
        onUpdate: (t) => {
          c.sprite.position.set(
            fromX + (target.x - fromX) * t,
            fromY + (target.y - fromY) * t,
          );
          c.sprite.alpha = fromAlpha + (0.2 - fromAlpha) * t;
        },
      });
    }, delayMs);
  }

  private scheduleDestroyAll(list: ActiveChip[], delayMs: number): void {
    window.setTimeout(() => {
      for (const c of list) {
        try {
          c.sprite.destroy();
        } catch {
          /* already destroyed */
        }
      }
    }, delayMs);
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
